import { db } from '../../db';
import { printerApi } from '../../api';
import { encodePrintableInvoice, encodeTestPrint } from './escposEncoder';
import { WebBluetoothPrinterExecutor } from './executors/WebBluetoothExecutor';
import { ElectronPrinterExecutor } from './executors/ElectronExecutor';
import { CapacitorBluetoothPrinterExecutor } from './executors/CapacitorBluetoothExecutor';
import { UnavailablePrinterExecutor } from './executors/UnavailableExecutor';
import { RemotePrinterHostAgent } from './RemotePrinterHostAgent';

const CONNECTION_METADATA_KEY = 'printer:lastConnection';
const QUEUE_METADATA_KEY = 'printer:jobQueue';
const HOST_IDENTITY_METADATA_KEY = 'printer:hostIdentity';

const defaultCapabilities = () => ({
  canDiscover: false,
  canPair: false,
  canPrint: false,
  transports: [],
  persistentConnection: false,
  notes: [],
});

const clone = (value) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map((item) => clone(item));
  }
  if (typeof value === 'object') {
    return { ...value };
  }
  return value;
};

const readMetadata = async (key, fallback = null) => {
  try {
    const value = await db.getMetadata(key);
    if (value !== undefined && value !== null) {
      return value;
    }
  } catch (error) {
    console.warn('[PrinterService] Failed to read Dexie metadata', key, error);
  }

  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch (error) {
      console.warn('[PrinterService] Failed to read localStorage metadata', key, error);
    }
  }

  return fallback;
};

const writeMetadata = async (key, value) => {
  try {
    await db.setMetadata(key, value);
  } catch (error) {
    console.warn('[PrinterService] Failed to write Dexie metadata', key, error);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (storageError) {
        console.error('[PrinterService] Failed to persist metadata fallback', key, storageError);
      }
    }
  }
};

const createJobId = () =>
  `printjob-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const hasAuthToken = () => {
  try {
    return !!(localStorage?.getItem('authToken') || localStorage?.getItem('token'));
  } catch {
    return false;
  }
};

const bytesToBase64 = (bytes) => {
  if (!(bytes instanceof Uint8Array)) return '';
  const btoaImpl =
    typeof btoa === 'function'
      ? btoa
      : (typeof window !== 'undefined' && typeof window.btoa === 'function' ? window.btoa : null);
  if (!btoaImpl) {
    throw new Error('Base64 encoding not supported in this environment.');
  }
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoaImpl(binary);
};

class PrinterService {
  constructor() {
    const environment = this.detectEnvironment();
    this.listeners = new Set();
    this.state = {
      environment,
      executorType: 'unavailable',
      connected: false,
      connecting: false,
      restoring: false,
      device: null,
      lastError: null,
      lastConnectedAt: null,
      context: null,
      persisted: null,
      queue: [],
      capabilities: defaultCapabilities(),
      hostIdentity: null,
      remoteHost: {
        supported: environment === 'electron',
        enabled: false,
        status: environment === 'electron' ? 'idle' : 'unsupported',
        hostId: null,
        deviceId: null,
        printerName: null,
        lastJob: null,
        error: null,
      },
    };

    this.hostIdentity = null;
    this.userContext = null;

    this.executor = this.createExecutor(environment);
    this.state.executorType = this.executor.type;
    this.state.capabilities = clone(this.executor.getCapabilities());

    if (environment === 'electron') {
      this.remoteHostAgent = new RemotePrinterHostAgent({
        getHostIdentity: () => this.hostIdentity,
        getDevice: () => this.state.device,
        onStateChange: (remoteState) => this.setRemoteHostState(remoteState),
        printRaw: (payload, options) => this.performLocalRawPrint(payload, { ...options, allowQueue: false }),
      });
    } else {
      this.remoteHostAgent = null;
    }

    this.initialized = false;
    this.initPromise = this.bootstrap();
  }

  detectEnvironment() {
    if (typeof window !== 'undefined') {
      if (window.electronAPI?.isElectron) {
        return 'electron';
      }
      const Capacitor = window.Capacitor;
      if (Capacitor) {
        const platform =
          typeof Capacitor.getPlatform === 'function'
            ? Capacitor.getPlatform()
            : Capacitor.platform;
        if (platform === 'android') {
          return 'capacitor-android';
        }
        if (platform === 'ios') {
          return 'capacitor-ios';
        }
        if (Capacitor.isNativePlatform?.()) {
          return 'capacitor-native';
        }
      }
    }

    if (typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent || '')) {
      return 'electron';
    }

    if (typeof process !== 'undefined' && process.versions?.electron) {
      return 'electron';
    }

    return 'web';
  }

  deriveHostDeviceName() {
    try {
      if (typeof navigator !== 'undefined') {
        const platform = navigator.userAgentData?.platform || navigator.platform || 'desktop';
        const vendor = navigator.vendor || '';
        return `Desktop (${platform}${vendor ? ` • ${vendor}` : ''})`;
      }
    } catch {}
    return 'TrackProfit Desktop';
  }

  async generateHostIdentity() {
    return {
      deviceId: `desktop-${Math.random().toString(36).slice(2, 10)}`,
      deviceName: this.deriveHostDeviceName(),
      createdAt: Date.now(),
    };
  }

  createExecutor(environment) {
    switch (environment) {
      case 'electron':
        return new ElectronPrinterExecutor();
      case 'capacitor-android':
        return new CapacitorBluetoothPrinterExecutor();
      case 'capacitor-ios':
      case 'capacitor-native':
        return new UnavailablePrinterExecutor(environment);
      case 'web':
      default:
        return new WebBluetoothPrinterExecutor();
    }
  }

  async bootstrap() {
    this.updateState({ restoring: true }, { silent: true });
    const [persistedConnection, queuedJobs, storedHostIdentity] = await Promise.all([
      readMetadata(CONNECTION_METADATA_KEY, null),
      readMetadata(QUEUE_METADATA_KEY, []),
      readMetadata(HOST_IDENTITY_METADATA_KEY, null),
    ]);

    let hostIdentity = storedHostIdentity;
    if (!hostIdentity?.deviceId) {
      hostIdentity = await this.generateHostIdentity();
      await writeMetadata(HOST_IDENTITY_METADATA_KEY, hostIdentity);
    }
    this.hostIdentity = hostIdentity;

    const patch = {
      restoring: false,
      persisted: persistedConnection,
      queue: Array.isArray(queuedJobs) ? queuedJobs : [],
      hostIdentity: clone(hostIdentity),
    };

    if (persistedConnection?.device) {
      patch.device = persistedConnection.device;
      patch.lastConnectedAt = persistedConnection.lastConnectedAt || null;
      patch.context = persistedConnection.context || null;
    }

    this.updateState(patch);
    this.initialized = true;

    if (this.remoteHostAgent && this.userContext) {
      this.remoteHostAgent.setUserContext(this.userContext);
    }

    return this.getState();
  }

  async ensureInitialized() {
    if (!this.initialized) {
      await this.initPromise;
    }
  }

  getState() {
    return {
      ...this.state,
      device: clone(this.state.device),
      capabilities: clone(this.state.capabilities),
      persisted: clone(this.state.persisted),
      queue: clone(this.state.queue),
    };
  }

  subscribe(listener) {
    if (typeof listener !== 'function') {
      throw new Error('PrinterService.subscribe expects a function listener');
    }
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  notify() {
    const snapshot = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (error) {
        console.error('[PrinterService] Listener error', error);
      }
    });
  }

  updateState(patch, options = {}) {
    const nextState = { ...this.state, ...patch };
    if (patch.capabilities) {
      nextState.capabilities = clone(patch.capabilities);
    }
    if (patch.device !== undefined) {
      nextState.device = clone(patch.device);
    }
    if (patch.queue !== undefined) {
      nextState.queue = Array.isArray(patch.queue) ? patch.queue.map((job) => clone(job)) : [];
    }
    if (patch.remoteHost) {
      nextState.remoteHost = clone(patch.remoteHost);
    }
    if (patch.hostIdentity) {
      nextState.hostIdentity = clone(patch.hostIdentity);
    }
    this.state = nextState;
    if (!options.silent) {
      this.notify();
    }
  }

  setRemoteHostState(remoteState) {
    if (!remoteState) return;
    this.updateState({ remoteHost: remoteState });
  }

  setUserContext(user) {
    this.ensureInitialized()
      .then(() => {
        this.userContext = user && user.userId
          ? {
              userId: user.userId,
              name: user.name || null,
              phone: user.phone || null,
            }
          : null;
        if (this.remoteHostAgent) {
          this.remoteHostAgent.setUserContext(this.userContext);
        }
      })
      .catch(() => {});
  }

  async persistConnection() {
    const payload = {
      environment: this.state.environment,
      executorType: this.state.executorType,
      device: this.state.device,
      connected: this.state.connected,
      lastConnectedAt: this.state.lastConnectedAt,
      context: this.state.context,
    };
    await writeMetadata(CONNECTION_METADATA_KEY, payload);
    this.updateState({ persisted: payload }, { silent: true });
  }

  async persistQueue() {
    await writeMetadata(QUEUE_METADATA_KEY, this.state.queue);
  }

  async discoverDevices(options = {}) {
    await this.ensureInitialized();
    const capabilities = this.executor.getCapabilities();
    if (!capabilities.canDiscover) {
      return {
        success: false,
        devices: [],
        message: 'Discovery is not supported in this environment.',
        capabilities,
      };
    }
    const result = await this.executor.discoverDevices(options);
    return {
      ...(result || {}),
      capabilities,
    };
  }

  async connect(deviceDescriptor, options = {}) {
    await this.ensureInitialized();
    const capabilities = this.executor.getCapabilities();
    if (!capabilities.canPair && !capabilities.canPrint) {
      throw new Error('Printer connection is not supported in this environment.');
    }

    this.updateState({ connecting: true, lastError: null });
    try {
      const result = await this.executor.connect(deviceDescriptor, options);
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to connect to printer.');
      }

      const device = result.device || deviceDescriptor || null;
      const now = Date.now();
      this.updateState({
        connecting: false,
        connected: true,
        device,
        lastConnectedAt: now,
        lastError: null,
        context: options.context || this.state.context || null,
      });
      await this.persistConnection();
      if (this.remoteHostAgent) {
        this.remoteHostAgent.onPrinterStateChange({ connected: true }).catch(() => {});
      }
      return { success: true, device };
    } catch (error) {
      this.updateState({
        connecting: false,
        connected: false,
        lastError: error.message || String(error),
      });
      throw error;
    }
  }

  async restorePersistedConnection(options = {}) {
    await this.ensureInitialized();
    if (!this.state.persisted?.device) {
      return { success: false, restored: false, message: 'No persisted printer found.' };
    }

    if (options.autoReconnect === false) {
      return { success: true, restored: false };
    }

    try {
      const result = await this.executor.restoreConnection(this.state.persisted, options);
      if (result?.success && result.connected) {
        this.updateState({
          connected: true,
          device: result.device || this.state.persisted.device,
          lastError: null,
        });
        await this.persistConnection();
        if (this.remoteHostAgent) {
          this.remoteHostAgent.onPrinterStateChange({ connected: true }).catch(() => {});
        }
        return { success: true, restored: true, device: this.state.device };
      }
      return { success: false, restored: false, message: result?.message || 'Unable to restore printer connection.' };
    } catch (error) {
      this.updateState({ lastError: error.message || String(error) });
      return { success: false, restored: false, message: error.message || String(error) };
    }
  }

  async disconnect() {
    await this.ensureInitialized();
    try {
      const result = await this.executor.disconnect();
      this.updateState({ connected: false });
      await this.persistConnection();
      if (this.remoteHostAgent) {
        this.remoteHostAgent.onPrinterStateChange({ connected: false }).catch(() => {});
      }
      return result;
    } catch (error) {
      this.updateState({ connected: false, lastError: error.message || String(error) });
      await this.persistConnection();
      if (this.remoteHostAgent) {
        this.remoteHostAgent.onPrinterStateChange({ connected: false }).catch(() => {});
      }
      throw error;
    }
  }

  async printInvoice(printableInvoice, options = {}) {
    await this.ensureInitialized();
    const capabilities = this.executor.getCapabilities();
    if (!capabilities.canPrint) {
      throw new Error('Printing is not supported in this environment.');
    }

    const payloadFactory = (() => {
      let cached;
      return () => {
        if (!cached) {
          cached = encodePrintableInvoice(printableInvoice, options.encoder || {});
        }
        return cached;
      };
    })();

    if (!this.state.connected) {
      let remoteError = null;
      let remoteResult = null;
      if (this.state.environment !== 'electron') {
        try {
          remoteResult = await this.tryRemotePrintPayload(payloadFactory, {
            payloadType: 'escpos',
            contentType: 'application/vnd.trackprofit.escpos',
            meta: {
              ...(printableInvoice?.metadata || {}),
              ...(options.meta || {}),
              source: options.meta?.source || 'printerService.printInvoice',
            },
            title:
              options.title ||
              printableInvoice?.metadata?.billName ||
              printableInvoice?.metadata?.name ||
              'Invoice',
            remote: options.remote,
          });
        } catch (error) {
          remoteError = error;
        }
      }

      if (remoteResult) {
        return remoteResult;
      }

      if (options.allowQueue !== false) {
        const job = await this.enqueueJob(printableInvoice, {
          reason: remoteError ? 'remote-printer-unavailable' : 'printer-not-connected',
          mode: 'invoice',
          remoteError: remoteError?.message || null,
        });
        return {
          success: false,
          queued: true,
          job,
          remoteFallback: Boolean(remoteError),
          remoteError: remoteError?.message,
        };
      }

      throw remoteError || new Error('Printer is not connected. Please connect to a printer or enable offline queueing.');
    }

    return this.performLocalRawPrint(payloadFactory(), options);
  }

  async performLocalRawPrint(rawBytes, options = {}) {
    const data =
      rawBytes instanceof Uint8Array
        ? rawBytes
        : Array.isArray(rawBytes)
          ? Uint8Array.from(rawBytes)
          : new Uint8Array(rawBytes || []);
    const result = await this.executor.print(data, options);
    if (!result?.success) {
      this.updateState({ lastError: result?.message || 'Print failed.' });
    }
    return result;
  }

  async tryRemotePrintPayload(resolvePayload, options = {}) {
    if (options?.allowRemote === false) return null;
    if (this.state.environment === 'electron') return null;
    if (!hasAuthToken()) return null;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;

    const payloadBytes = typeof resolvePayload === 'function' ? resolvePayload() : resolvePayload;
    if (!payloadBytes || !payloadBytes.length) {
      return null;
    }

    const data =
      payloadBytes instanceof Uint8Array
        ? payloadBytes
        : Array.isArray(payloadBytes)
          ? Uint8Array.from(payloadBytes)
          : new Uint8Array(payloadBytes);

    try {
      const job = await printerApi.enqueueJob({
        deviceId: options.remote?.deviceId || options.deviceId,
        payload: {
          type: options.payloadType || 'raw',
          encoding: 'base64',
          data: bytesToBase64(data),
        },
        printer: options.printer || null,
        contentType: options.contentType || 'application/octet-stream',
        meta: options.meta || {},
        title: options.title || 'Print Job',
      });
      return { success: true, remote: true, job };
    } catch (error) {
      const remoteError = new Error(error?.message || 'Desktop printer is unavailable.');
      remoteError.code = 'REMOTE_PRINT_FAILED';
      remoteError.details = error;
      throw remoteError;
    }
  }

  async printRaw(rawBytes, options = {}) {
    await this.ensureInitialized();
    const capabilities = this.executor.getCapabilities();
    if (!capabilities.canPrint) {
      throw new Error('Printing is not supported in this environment.');
    }

    if (!this.state.connected) {
      let remoteError = null;
      let remoteResult = null;
      if (this.state.environment !== 'electron') {
        try {
          remoteResult = await this.tryRemotePrintPayload(
            () =>
              rawBytes instanceof Uint8Array
                ? rawBytes
                : Array.isArray(rawBytes)
                  ? Uint8Array.from(rawBytes)
                  : new Uint8Array(rawBytes),
            {
              payloadType: 'raw',
              contentType: options.contentType || 'application/octet-stream',
              meta: {
                ...(options.meta || {}),
                source: options.meta?.source || 'printerService.printRaw',
              },
              title: options.title || 'Raw Print Job',
              remote: options.remote,
            }
          );
        } catch (error) {
          remoteError = error;
        }
      }

      if (remoteResult) {
        return remoteResult;
      }

      if (options.allowQueue !== false) {
        const job = await this.enqueueJob(
          { type: 'raw', payload: Array.from(rawBytes) },
          {
            reason: remoteError ? 'remote-printer-unavailable' : 'printer-not-connected',
            mode: 'raw',
            remoteError: remoteError?.message || null,
          },
        );
        return {
          success: false,
          queued: true,
          job,
          remoteFallback: Boolean(remoteError),
          remoteError: remoteError?.message,
        };
      }

      throw remoteError || new Error('Printer is not connected. Please connect to a printer or enable offline queueing.');
    }

    return this.performLocalRawPrint(rawBytes, options);
  }

  async testPrint(options = {}) {
    await this.ensureInitialized();
    const capabilities = this.executor.getCapabilities();
    if (!capabilities.canPrint) {
      throw new Error('Printing is not supported in this environment.');
    }
    const payload = encodeTestPrint({
      businessName: options.businessName || this.state.persisted?.device?.name || 'TrackProfit',
      autoCut: options.autoCut,
      feedLines: options.feedLines,
      cutMode: options.cutMode,
    });
    return this.printRaw(payload, options);
  }

  async getStatus() {
    await this.ensureInitialized();
    try {
      const executorStatus = await this.executor.getStatus();
      return {
        ...executorStatus,
        state: this.getState(),
      };
    } catch (error) {
      return {
        connected: this.state.connected,
        message: error.message || String(error),
        error,
        state: this.getState(),
      };
    }
  }

  async enqueueJob(printableInvoice, options = {}) {
    await this.ensureInitialized();
    const job = {
      id: createJobId(),
      createdAt: Date.now(),
      printable: printableInvoice,
      options,
      status: 'pending',
    };
    const queue = [...this.state.queue, job];
    this.updateState({ queue });
    await this.persistQueue();
    return job;
  }

  async markJobComplete(jobId, status = 'completed') {
    await this.ensureInitialized();
    const queue = this.state.queue.map((job) =>
      job.id === jobId ? { ...job, status, completedAt: Date.now() } : job
    );
    this.updateState({ queue });
    await this.persistQueue();
  }

  async removeJob(jobId) {
    await this.ensureInitialized();
    const queue = this.state.queue.filter((job) => job.id !== jobId);
    this.updateState({ queue });
    await this.persistQueue();
  }

  async clearQueue() {
    await this.ensureInitialized();
    this.updateState({ queue: [] });
    await this.persistQueue();
  }

  async flushQueue(options = {}) {
    await this.ensureInitialized();
    if (!this.state.queue.length) {
      return { processed: 0, failed: 0 };
    }
    if (!this.state.connected) {
      return { processed: 0, failed: this.state.queue.length, message: 'Printer not connected.' };
    }

    let processed = 0;
    let failed = 0;

    for (const job of this.state.queue) {
      try {
        if (job.printable?.type === 'raw') {
          const payload = Uint8Array.from(job.printable.payload || []);
          await this.printRaw(payload, { ...options, allowQueue: false });
        } else {
          await this.printInvoice(job.printable, { ...options, allowQueue: false });
        }
        // eslint-disable-next-line no-await-in-loop
        await this.markJobComplete(job.id, 'completed');
        // eslint-disable-next-line no-await-in-loop
        await this.removeJob(job.id);
        processed += 1;
      } catch (error) {
        failed += 1;
        // eslint-disable-next-line no-await-in-loop
        await this.markJobComplete(job.id, 'failed');
        this.updateState({ lastError: error.message || String(error) }, { silent: true });
      }
    }

    return { processed, failed };
  }
}

export const printerService = new PrinterService();

